import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, X, Thermometer, Wind, Camera, Cpu, Zap, Activity, Search, Droplets, Gauge, Radio, CheckCircle, AlertTriangle } from "lucide-react";
import ConditionModal from "../components/modals/ConditionModal";
import { noeudsAPI, evenementsAPI, automatisationsAPI } from "../api/services";

const operateurs = [
  { id: "op-ET", label: "ET", symbol: "∧", color: "#22c55e", bg: "#dcfce7" },
  { id: "op-OU", label: "OU", symbol: "∨", color: "#f59e0b", bg: "#fef3c7" },
  { id: "op-NON", label: "NON", symbol: "¬", color: "#ef4444", bg: "#fee2e2" },
  { id: "op-OUEXCL", label: "OU EXCL.", symbol: "⊕", color: "#8b5cf6", bg: "#ede9fe" },
  { id: "op-NONET", label: "NON-ET", symbol: "⊼", color: "#ec4899", bg: "#fce7f3" },
  { id: "op-NONOU", label: "NON-OU", symbol: "⊽", color: "#14b8a6", bg: "#ccfbf1" },
];

// Mapping type noeud → icône
const typeIconMap = {
  "Capteur Température": Thermometer,
  "Capteur Humidité": Droplets,
  "Capteur CO2": Wind,
  "Capteur Pression": Gauge,
  "Capteur Qualité Air": Wind,
  "Caméra IP": Camera,
  "Moteur": Activity,
  "Relais": Zap,
  "Thermostat": Thermometer,
  "Vanne": Gauge,
  "Ventilateur": Wind,
  "Climatiseur": Wind,
  "Lumière": Zap,
};
const getIcon = (type) => typeIconMap[type] || Radio;

// ── Composant nœud sur le canvas ──────────────────────────────
function CanvasNode({ node, onDragStart, onPortClick, connecting, onDelete, onNodeUpdate }) {
  const isOp = node.nodeType === "operateur";
  const isCapteur = node.nodeType === "capteur";
  const isConnecting = connecting?.fromId === node.instanceId;

  const portStyle = (color) => ({
    width: 14, height: 14,
    borderRadius: "50%",
    background: color,
    border: "2px solid white",
    boxShadow: "0 0 0 2px " + color,
    cursor: "crosshair",
    position: "absolute",
    zIndex: 20,
  });

  return (
    <div
      style={{ position: "absolute", left: node.x, top: node.y, userSelect: "none" }}
      onMouseDown={(e) => { e.stopPropagation(); onDragStart(e, node.instanceId); }}
    >
      {/* Bouton supprimer */}
      <button
        onMouseDown={e => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(node.instanceId); }}
        style={{ position: "absolute", top: -8, right: -8, zIndex: 30, width: 20, height: 20, borderRadius: "50%", background: "#ef4444", color: "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: "bold" }}
      >×</button>

      {isOp ? (
        <div style={{ position: "relative", width: 80, height: 80 }}>
          {/* Port entrée gauche */}
          <div
            style={{ ...portStyle(node.color), left: -7, top: "50%", transform: "translateY(-50%)" }}
            onMouseDown={e => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onPortClick(node.instanceId, "in"); }}
          />
          {/* Corps opérateur */}
          <div
            className="w-full h-full rounded-2xl flex flex-col items-center justify-center border-2"
            style={{ background: node.bg, borderColor: node.color }}
          >
            <span className="text-2xl font-bold" style={{ color: node.color }}>{node.symbol}</span>
            <span className="text-xs font-bold mt-1" style={{ color: node.color }}>{node.label}</span>
          </div>
          {/* Port sortie droite */}
          <div
            style={{ ...portStyle(node.color), right: -7, top: "50%", transform: "translateY(-50%)" }}
            onMouseDown={e => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onPortClick(node.instanceId, "out"); }}
          />
        </div>
      ) : (
        <div style={{ position: "relative", width: 112 }}>
          {/* Port capteur = sortie droite / actionneur = entrée gauche */}
          <div
            style={{
              ...portStyle(isCapteur ? "#3b82f6" : "#f97316"),
              ...(isCapteur ? { right: -7 } : { left: -7 }),
              top: "50%",
              transform: "translateY(-50%)",
            }}
            onMouseDown={e => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onPortClick(node.instanceId, isCapteur ? "out" : "in"); }}
          />
          {/* Corps capteur/actionneur */}
          <div
            className={`rounded-2xl border-2 p-2 flex flex-col items-center gap-1 ${isConnecting ? "ring-2 ring-offset-1 ring-blue-400" : ""}`}
            style={{
              background: isCapteur ? "#eff6ff" : "#fff7ed",
              borderColor: isCapteur ? "#93c5fd" : "#fdba74",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: isCapteur ? "#dbeafe" : "#ffedd5" }}
            >
              <node.icon size={18} style={{ color: isCapteur ? "#3b82f6" : "#f97316" }} />
            </div>
            <p className="text-xs font-bold text-slate-700 text-center break-all leading-tight w-full" title={node.id}>{node.id}</p>
            <p className="text-[10px] text-slate-400 text-center leading-tight">{node.label}</p>
            {!isCapteur && (
              <select
                value={node.action || "Activer"}
                onChange={(e) => { e.stopPropagation(); onNodeUpdate(node.instanceId, { action: e.target.value }); }}
                onMouseDown={e => e.stopPropagation()}
                className="text-[10px] font-semibold rounded-md px-1 py-0.5 border mt-0.5 cursor-pointer"
                style={{
                  background: (node.action || "Activer") === "Activer" ? "#dcfce7" : "#fee2e2",
                  borderColor: (node.action || "Activer") === "Activer" ? "#86efac" : "#fca5a5",
                  color: (node.action || "Activer") === "Activer" ? "#166534" : "#991b1b",
                }}
              >
                <option value="Activer">Activer</option>
                <option value="Désactiver">Désactiver</option>
              </select>
            )}
            {isCapteur && <div className="w-2 h-2 rounded-full bg-green-400" />}
          </div>
        </div>
      )}
    </div>
  );
}



// ── Page principale ───────────────────────────────────────────
export default function CreerEvenement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id") || "";
  const projetId = searchParams.get("projet") || "";
  const projetNom = searchParams.get("projetNom") || "Projet";
  const revendeurId = searchParams.get("revendeur") || "";
  const clientId = searchParams.get("client") || "";
  const isEdit = !!editId;

  const [nom, setNom] = useState("");
  const [priorite, setPriorite] = useState("Normale");
  const [cooldown, setCooldown] = useState(5);
  const [searchCapteur, setSearchCapteur] = useState("");
  const [searchActionneur, setSearchActionneur] = useState("");
  const [capteurs, setCapteurs] = useState([]);
  const [actionneurs, setActionneurs] = useState([]);
  const [loadingNoeuds, setLoadingNoeuds] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: "success"|"error", message }
  const [nomTouched, setNomTouched] = useState(false);

  // Charger les noeuds du projet sélectionné
  useEffect(() => {
    if (!projetId) return;
    setLoadingNoeuds(true);
    noeudsAPI.getAll({ projet: projetId })
      .then(res => {
        const list = res.data?.data || res.data || [];
        const caps = list
          .filter(n => n.categorie === "Capteur")
          .map(n => ({ id: n.sn, label: n.type?.replace("Capteur ", "") || n.sn, type: n.type, icon: getIcon(n.type), color: "#3b82f6", _id: n._id }));
        const acts = list
          .filter(n => n.categorie === "Actionneur")
          .map(n => ({ id: n.sn, label: n.type || n.sn, type: n.type, icon: getIcon(n.type), color: "#f97316", _id: n._id }));
        setCapteurs(caps);
        setActionneurs(acts);
      })
      .catch(err => console.error("Erreur chargement noeuds:", err))
      .finally(() => setLoadingNoeuds(false));
  }, [projetId]);

  // Charger l'événement existant en mode édition
  useEffect(() => {
    if (!editId) return;
    setLoadingEvent(true);
    evenementsAPI.getOne(editId)
      .then(res => {
        const evt = res.data?.data || res.data;
        if (evt) {
          setNom(evt.nom || "");
          setPriorite(evt.priorite || "Normale");
          setCooldown(evt.cooldown ?? 5);
          if (evt.nodes && evt.nodes.length > 0) {
            const restored = evt.nodes.map(n => ({
              ...n,
              icon: getIcon(n.type || (n.nodeType === "capteur" ? `Capteur ${n.label}` : n.label)),
              color: n.nodeType === "capteur" ? "#3b82f6" : n.nodeType === "actionneur" ? "#f97316" : undefined,
              bg: n.nodeType === "operateur" ? operateurs.find(o => o.label === n.label)?.bg : undefined,
              symbol: n.nodeType === "operateur" ? operateurs.find(o => o.label === n.label)?.symbol : undefined,
            }));
            setNodes(restored);
            const maxNum = restored.reduce((max, n) => {
              const parts = n.instanceId.split("-");
              const num = parseInt(parts[parts.length - 1], 10);
              return isNaN(num) ? max : Math.max(max, num);
            }, 0);
            instanceCounter.current = maxNum;
          }
          if (evt.connections) setConnections(evt.connections);
          if (evt.pauseAutomatisations) {
            setPauseAutoIds(evt.pauseAutomatisations.map(a => a._id || a));
          }
        }
      })
      .catch(err => console.error("Erreur chargement événement:", err))
      .finally(() => setLoadingEvent(false));
  }, [editId]);

  // Canvas
  const canvasRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState(null); // { fromId, fromPort, fromLabel }
  const [pendingConn, setPendingConn] = useState(null);
  const [showCondModal, setShowCondModal] = useState(false);
  const dragging = useRef(null);

  const instanceCounter = useRef(0);

  // Ajouter un nœud au canvas
  const addNode = (item, type) => {
    instanceCounter.current += 1;
    const id = `${item.id}-${instanceCounter.current}`;
    const x = 80 + Math.random() * 400;
    const y = 60 + Math.random() * 200;
    setNodes(prev => [...prev, { ...item, instanceId: id, nodeType: type, x, y }]);
  };

  // Drag dans le canvas
  const onDragStart = (e, instanceId) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const node = nodes.find(n => n.instanceId === instanceId);
    dragging.current = {
      instanceId,
      offsetX: e.clientX - rect.left - node.x,
      offsetY: e.clientY - rect.top - node.y,
    };
  };

  const onMouseMove = useCallback((e) => {
    const d = dragging.current;
    if (!d) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left - d.offsetX;
    const y = e.clientY - rect.top - d.offsetY;
    setNodes(prev => prev.map(n => n.instanceId === d.instanceId ? { ...n, x: Math.max(0, x), y: Math.max(0, y) } : n));
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = null; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, [onMouseMove, onMouseUp]);

  // Clic sur un port
  const onPortClick = (instanceId, port) => {
    const node = nodes.find(n => n.instanceId === instanceId);
    if (!connecting) {
      // Démarrer connexion depuis ce port
      setConnecting({ fromId: instanceId, fromPort: port, fromLabel: node.id || node.label });
    } else {
      if (connecting.fromId === instanceId) { setConnecting(null); return; }
      // Terminer connexion
      const toNode = nodes.find(n => n.instanceId === instanceId);
      setPendingConn({
        fromId: connecting.fromId, fromLabel: connecting.fromLabel,
        toId: instanceId, toLabel: toNode.id || toNode.label,
        fromPort: connecting.fromPort, toPort: port,
      });
      setConnecting(null);
      setShowCondModal(true);
    }
  };

  const validateCondition = ({ condition, valeur, action }) => {
    const id = `conn-${Date.now()}`;
    setConnections(prev => [...prev, { ...pendingConn, id, condition, valeur, action }]);
    setShowCondModal(false);
    setPendingConn(null);
  };

  const deleteNode = (instanceId) => {
    setNodes(prev => prev.filter(n => n.instanceId !== instanceId));
    setConnections(prev => prev.filter(c => c.fromId !== instanceId && c.toId !== instanceId));
  };

  const deleteConnection = (connId) => setConnections(prev => prev.filter(c => c.id !== connId));
  const [editingConn, setEditingConn] = useState(null);

  // Calcul centre d'un nœud pour les lignes SVG
  const getNodeCenter = (instanceId) => {
    const n = nodes.find(x => x.instanceId === instanceId);
    if (!n) return { x: 0, y: 0 };
    const w = n.nodeType === "operateur" ? 80 : 112;
    const h = n.nodeType === "operateur" ? 80 : 96;
    return { x: n.x + w / 2, y: n.y + h / 2 };
  };

const getPortPos = (instanceId, port) => {
  const n = nodes.find(x => x.instanceId === instanceId);
  if (!n) return { x: -999, y: -999 };
  if (n.nodeType === "operateur") {
    return port === "in"
      ? { x: n.x, y: n.y + 40 }
      : { x: n.x + 80, y: n.y + 40 };
  }
  if (n.nodeType === "capteur") return { x: n.x + 112, y: n.y + 48 };
  return { x: n.x, y: n.y + 48 }; // actionneur : port gauche
};
  const [conflictWarning, setConflictWarning] = useState(null);

  // Automatisations du projet (pour mise en pause configurable)
  const [projectAutos, setProjectAutos] = useState([]);
  const [pauseAutoIds, setPauseAutoIds] = useState([]);

  // Charger toutes les automatisations actives du projet
  useEffect(() => {
    if (!projetId) { setProjectAutos([]); return; }
    automatisationsAPI.getAll({ projet: projetId })
      .then(res => {
        const autos = (res.data?.data || []).filter(a => a.statut === 'Actif');
        setProjectAutos(autos);
      })
      .catch(() => setProjectAutos([]));
  }, [projetId]);

  // Pré-cocher les automatisations impactées quand les connexions changent.
  // En mode édition, on ne pré-coche pas : le state sauvegardé est la source de vérité,
  // sinon le user voit des cases "cochées" qui n'ont jamais été persistées.
  useEffect(() => {
    if (isEdit) return;
    if (!projectAutos.length || connections.length === 0) return;

    // Trouver les actionneurs ciblés par cet événement
    const actionneurNodes = nodes.filter(n => n.nodeType === 'actionneur');
    const targetActIds = new Set();
    connections.forEach(conn => {
      const toNode = actionneurNodes.find(n => n.instanceId === conn.toId);
      if (toNode) targetActIds.add(toNode._id || toNode.id);
    });

    if (targetActIds.size === 0) return;

    // Trouver les _id MongoDB des actionneurs ciblés
    const targetSns = [...targetActIds];
    const targetDbIds = actionneurs
      .filter(a => targetSns.includes(a.id) || targetSns.includes(a._id))
      .map(a => a._id)
      .filter(Boolean);

    // Pré-cocher les automatisations qui utilisent ces actionneurs
    const suggestedIds = projectAutos
      .filter(auto => {
        const autoActIds = (auto.actionneurs || []).map(a => a._id || a);
        return autoActIds.some(id => targetDbIds.includes(id));
      })
      .map(a => a._id);

    // Merge avec les IDs déjà cochés (ne pas décocher ce que le user a coché manuellement)
    setPauseAutoIds(prev => [...new Set([...prev, ...suggestedIds])]);
  }, [connections, nodes, projectAutos, actionneurs, isEdit]);

  const buildPayload = () => ({
    nom,
    priorite,
    cooldown: parseInt(cooldown) || 0,
    projet: projetId,
    revendeur: revendeurId || null,
    client: clientId || null,
    nodes: nodes.map(n => ({ instanceId: n.instanceId, nodeType: n.nodeType, id: n.id, label: n.label, type: n.type, symbol: n.symbol, action: n.action, x: n.x, y: n.y, _id: n._id })),
    connections: connections.map(c => ({ id: c.id, fromId: c.fromId, toId: c.toId, fromPort: c.fromPort, toPort: c.toPort, condition: c.condition, valeur: c.valeur, action: c.action })),
    pauseAutomatisations: pauseAutoIds,
    statut: "Actif",
  });

  const doSave = async (payload) => {
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await evenementsAPI.update(editId, payload);
        setToast({ type: "success", message: t('createEvent.successUpdated') });
      } else {
        res = await evenementsAPI.create(payload);
        setToast({ type: "success", message: t('createEvent.successCreated') });
      }
      // Afficher warning s'il y en a
      if (res.data?.warnings?.length > 0) {
        setToast({ type: "warning", message: t('createEvent.conflictsWarning', { count: res.data.warnings.length }) });
      }
      setTimeout(() => navigate("/evenements"), 2000);
    } catch (err) {
      console.error("Erreur sauvegarde événement:", err);
      setToast({ type: "error", message: err.response?.data?.error || t('createEvent.saveError') });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    const payload = buildPayload();

    // Vérifier les conflits avant de sauvegarder
    try {
      const checkParams = isEdit ? `?exclude=${editId}` : '';
      const res = await evenementsAPI.checkConflicts(payload);
      const { conflicts } = res.data?.data || {};

      if (conflicts && conflicts.length > 0) {
        const errors = conflicts.filter(c => c.severity === 'error');
        const warnings = conflicts.filter(c => c.severity === 'warning');

        // Seules les erreurs bloquent — les warnings sont informatifs (événement prioritaire)
        if (errors.length > 0) {
          setConflictWarning(conflicts);
          return;
        }

        // Warnings uniquement → toast informatif et on sauvegarde
        if (warnings.length > 0) {
          setToast({ type: "warning", message: `${warnings.length} conflit(s) potentiel(s) détecté(s) avec des automatisations — l'événement reste prioritaire` });
        }
      }
    } catch (err) {
      // Si check échoue, on continue quand même
      console.error("Erreur vérification conflits:", err);
    }

    await doSave(payload);
  };

  const handleForceSave = async () => {
    setConflictWarning(null);
    await doSave(buildPayload());
  };

  // Aperçu langage naturel
  const buildPreview = () => {
    if (nodes.length === 0) return null;

    const nodeMap = Object.fromEntries(nodes.map(n => [n.instanceId, n]));
    const sensorConditions = [];
    const actionneurActions = [];

    // Conditions sur les capteurs (depuis les connexions sortant d'un capteur)
    connections.forEach(conn => {
      const fromNode = nodeMap[conn.fromId];
      if (fromNode?.nodeType === "capteur" && conn.condition && conn.valeur != null && conn.valeur !== "") {
        sensorConditions.push(`${fromNode.type || fromNode.label} ${fromNode.id.split("-")[1] || fromNode.id} ${conn.condition} ${conn.valeur}`);
      }
    });

    // Actions sur les actionneurs présents
    nodes.filter(n => n.nodeType === "actionneur").forEach(n => {
      const verb = (n.action || "Activer") === "Activer" ? t('createFunction.enable', 'Activer') : t('createFunction.disable', 'Désactiver');
      actionneurActions.push(`${verb} ${n.label} ${n.id.split("-")[1] || n.id}`);
    });

    // Présence d'opérateurs (juste pour indication)
    const ops = nodes.filter(n => n.nodeType === "operateur").map(n => n.label);

    if (sensorConditions.length === 0 && actionneurActions.length === 0) return null;

    const conditionText = sensorConditions.length > 0
      ? (ops.length > 0 ? sensorConditions.join(` [${ops.join(", ")}] `) : sensorConditions.join(" ET "))
      : "(aucune condition définie)";
    const actionText = actionneurActions.length > 0
      ? actionneurActions.join(" et ")
      : "(aucune action définie)";

    return { conditionText, actionText, sensorCount: sensorConditions.length, actionCount: actionneurActions.length };
  };

  const preview = buildPreview();

  const isValid = nom.trim().length > 0 && projetId;
  const btnDisabled = !isValid || nodes.length === 0 || saving;
  const nomError = nomTouched && !nom.trim();

  const filteredCapteurs = capteurs.filter(c => c.id.toLowerCase().includes(searchCapteur.toLowerCase()) || c.type.toLowerCase().includes(searchCapteur.toLowerCase()));
  const filteredActionneurs = actionneurs.filter(a => a.id.toLowerCase().includes(searchActionneur.toLowerCase()) || a.type.toLowerCase().includes(searchActionneur.toLowerCase()));

  const panelCard = "p-3 rounded-2xl border-2 border-dashed border-gray-200";

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/evenements")} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
          <ArrowLeft size={16} /> {t('createEvent.back')}
        </button>
        <div>
          <h1 className="font-bold text-slate-800 text-lg">{isEdit ? t('createEvent.editTitle') : t('createEvent.createTitle')}</h1>
          <p className="text-sm text-slate-400">{t('createEvent.project')} {projetNom}</p>
        </div>
      </div>

      {/* Nom + Priorité */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('createEvent.nameLabel')}
            </label>
            <input
              value={nom}
              onChange={e => setNom(e.target.value)}
              onBlur={() => setNomTouched(true)}
              placeholder="Ex: Allumer la lumière quand température > 25°C"
              className={`w-full px-4 py-3 border rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 ${
                nomError ? "border-red-300 focus:ring-red-400" : "border-gray-200 focus:ring-blue-500"
              }`}
            />
            {nomError && (
              <p className="text-red-500 text-xs mt-1.5">{t('createEvent.pleaseNameEvent')}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('createEvent.alertPriority')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setPriorite("Normale")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                  priorite === "Normale"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 text-slate-500 hover:border-gray-300"
                }`}
              >
                {t('createEvent.info')}
              </button>
              <button
                onClick={() => setPriorite("Critique")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                  priorite === "Critique"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-200 text-slate-500 hover:border-gray-300"
                }`}
              >
                <AlertTriangle size={14} /> {t('createEvent.critical')}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('createEvent.cooldown', 'Cooldown (min)')}</label>
            <input
              type="number"
              min="0"
              max="1440"
              value={cooldown}
              onChange={e => setCooldown(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1">{t('createEvent.cooldownHint', 'Délai minimum entre 2 déclenchements. 0 = pas de limite.')}</p>
          </div>
        </div>
      </div>

      {/* 3 Panels */}
      <div className="grid grid-cols-3 gap-4 mb-4">

        {/* Capteurs */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">{t('createEvent.sensors')} ({filteredCapteurs.length}/{capteurs.length})</p>
              <p className="text-xs text-slate-400">{t('createEvent.clickToAdd')}</p>
            </div>
          </div>
          <div className="relative mb-3">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchCapteur} onChange={e => setSearchCapteur(e.target.value)} placeholder={t('createEvent.searchSensor')} className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs bg-gray-50 focus:outline-none" />
          </div>
          <div className="grid grid-cols-4 gap-2 max-h-52 overflow-y-auto">
            {loadingNoeuds ? (
              <div className="col-span-4 py-4 text-center text-xs text-slate-400">{t('common.loading')}</div>
            ) : filteredCapteurs.length === 0 ? (
              <div className="col-span-4 py-4 text-center text-xs text-slate-400">{t('createEvent.noSensorsInProject')}</div>
            ) : filteredCapteurs.map(c => (
              <button key={c.id} onClick={() => addNode(c, "capteur")}
                className="flex flex-col items-center gap-1 p-2 bg-blue-50 border-2 border-blue-100 rounded-xl hover:border-blue-400 hover:bg-blue-100 transition-all">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <c.icon size={14} className="text-blue-600" />
                </div>
                <p className="text-xs font-bold text-slate-700">{c.id}</p>
                <p className="text-xs text-slate-400 leading-tight text-center">{c.label}</p>
                <div className="w-2 h-2 rounded-full bg-green-400" />
              </button>
            ))}
          </div>
        </div>

        {/* Opérateurs */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">{t('createEvent.operators')}</p>
              <p className="text-xs text-slate-400">AND, OR, NOT, XOR...</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {operateurs.map(op => (
              <button key={op.id} onClick={() => addNode(op, "operateur")}
                className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl border-2 hover:opacity-80 transition-all"
                style={{ background: op.bg, borderColor: op.color }}>
                <span className="text-xl font-bold" style={{ color: op.color }}>{op.symbol}</span>
                <span className="text-xs font-bold" style={{ color: op.color }}>{op.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actionneurs */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Cpu size={16} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">{t('createEvent.actuators')} ({filteredActionneurs.length}/{actionneurs.length})</p>
              <p className="text-xs text-slate-400">{t('createEvent.clickToAdd')}</p>
            </div>
          </div>
          <div className="relative mb-3">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchActionneur} onChange={e => setSearchActionneur(e.target.value)} placeholder={t('createEvent.searchActuator')} className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs bg-gray-50 focus:outline-none" />
          </div>
          <div className="grid grid-cols-4 gap-2 max-h-52 overflow-y-auto">
            {loadingNoeuds ? (
              <div className="col-span-4 py-4 text-center text-xs text-slate-400">{t('common.loading')}</div>
            ) : filteredActionneurs.length === 0 ? (
              <div className="col-span-4 py-4 text-center text-xs text-slate-400">{t('createEvent.noActuatorsInProject')}</div>
            ) : filteredActionneurs.map(a => (
              <button key={a.id} onClick={() => addNode(a, "actionneur")}
                className="flex flex-col items-center gap-1 p-2 bg-orange-50 border-2 border-orange-100 rounded-xl hover:border-orange-400 hover:bg-orange-100 transition-all">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <a.icon size={14} className="text-orange-600" />
                </div>
                <p className="text-xs font-bold text-slate-700">{a.id}</p>
                <p className="text-xs text-slate-400 leading-tight text-center">{a.label}</p>
                <div className="w-2 h-2 rounded-full bg-green-400" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Zone de connexion — banner */}
      <div className="rounded-t-2xl px-5 py-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #f97316 100%)" }}>
        <div>
          <p className="text-white font-semibold">{t('createEvent.connectionZone')} ({nodes.length} {t('createEvent.nodesLabel')})</p>
          <p className="text-blue-100 text-sm">{t('createEvent.connectionHint')}</p>
          {!projetId && (
            <p className="text-yellow-300 text-xs mt-1 flex items-center gap-1">{t('createEvent.noProjectSelected')}</p>
          )}
          {projetId && !nom.trim() && (
            <p className="text-yellow-300 text-xs mt-1 flex items-center gap-1">{t('createEvent.nameRequired')}</p>
          )}
          {connecting && (
            <p className="text-green-300 text-xs mt-1 animate-pulse">{t('createEvent.connectingHint')}</p>
          )}
        </div>
        <button
          disabled={btnDisabled}
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
            !btnDisabled
              ? "bg-white text-slate-800 hover:bg-gray-100"
              : "bg-white/20 text-white/50 cursor-not-allowed"
          }`}
        >
          <Save size={16} /> {saving ? t('createEvent.saving') : isEdit ? t('createEvent.update') : t('createEvent.save')}
        </button>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="bg-white border border-gray-200 rounded-b-2xl mb-6"
        style={{ minHeight: 320, position: "relative", overflow: "hidden",
                cursor: connecting ? "crosshair" : "default" }}
        //onClick={() => { if (connecting) setConnecting(null); }}
      >
        {/* SVG pour les connexions */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}>
          {connections.map(conn => {
            const from = getPortPos(conn.fromId, conn.fromPort);
            const to = getPortPos(conn.toId, conn.toPort);
            const mx = (from.x + to.x) / 2;
            return (
              <g key={conn.id}>
                <path
                  d={`M ${from.x} ${from.y} C ${mx} ${from.y}, ${mx} ${to.y}, ${to.x} ${to.y}`}
                  fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 3"
                />
                {conn.valeur && (
                  <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditingConn(conn); }}>
                    <rect x={mx - 30} y={(from.y + to.y) / 2 - 16} width="60" height="32" rx="8" fill="white" stroke={conn.action === "Activer" ? "#22c55e" : "#ef4444"} strokeWidth="1.5" style={{ pointerEvents: "all" }} />
                    <text x={mx} y={(from.y + to.y) / 2 - 3} textAnchor="middle" fontSize="9" fontWeight="bold" fill="#334155" style={{ pointerEvents: "none" }}>{conn.valeur}</text>
                    <text x={mx} y={(from.y + to.y) / 2 + 10} textAnchor="middle" fontSize="8" fill={conn.action === "Activer" ? "#16a34a" : "#dc2626"} style={{ pointerEvents: "none" }}>{conn.action || "—"}</text>
                    {/* Delete button */}
                    <circle cx={mx + 30} cy={(from.y + to.y) / 2 - 16} r="8" fill="#ef4444" style={{ pointerEvents: "all" }} onClick={(e) => { e.stopPropagation(); deleteConnection(conn.id); }} />
                    <text x={mx + 30} y={(from.y + to.y) / 2 - 12} textAnchor="middle" fontSize="10" fontWeight="bold" fill="white" style={{ pointerEvents: "none" }}>×</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {/* Nœuds */}
        {nodes.map(node => (
          <CanvasNode
            key={node.instanceId}
            node={node}
            onDragStart={onDragStart}
            onPortClick={onPortClick}
            connecting={connecting}
            onDelete={deleteNode}
            onNodeUpdate={(instanceId, updates) => {
              setNodes(prev => prev.map(n => n.instanceId === instanceId ? { ...n, ...updates } : n));
            }}
          />
        ))}

        {/* État vide */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-200">
            <Zap size={60} strokeWidth={1} />
            <p className="text-sm text-gray-300 mt-2">{t('createEvent.clickToAddNodes')}</p>
          </div>
        )}
      </div>

      {/* Aperçu en langage naturel */}
      {preview && (
        <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-5 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Activity size={16} className="text-teal-600" />
            </div>
            <p className="font-semibold text-slate-800">{t('createFunction.preview', 'Aperçu')}</p>
            <span className="text-[10px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">
              {t('createFunction.naturalLanguage', 'Langage naturel')}
            </span>
          </div>
          <div className="bg-white rounded-xl p-4 border border-teal-100">
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-teal-700">{t('createFunction.if', 'SI')} </span>
              <span className="text-blue-600 font-medium">{preview.conditionText}</span>
            </p>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">
              <span className="font-semibold text-orange-600">{t('createFunction.then', 'ALORS')} </span>
              <span className="font-medium">{preview.actionText}</span>
            </p>
          </div>
          <div className="flex gap-2 mt-3">
            <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{preview.sensorCount} condition{preview.sensorCount > 1 ? "s" : ""}</span>
            <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{preview.actionCount} action{preview.actionCount > 1 ? "s" : ""}</span>
          </div>
        </div>
      )}

      {/* Section : Automatisations à mettre en pause */}
      {projectAutos.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <Zap size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Mettre en pause des automatisations</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Quand cet événement se déclenche, les automatisations cochées seront mises en pause automatiquement.
                Un redémarrage manuel sera nécessaire depuis la page Automatisations.
              </p>
            </div>
            {pauseAutoIds.length > 0 && (
              <span className="ml-auto px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg text-xs font-bold text-amber-700 shrink-0">
                {pauseAutoIds.length} en pause
              </span>
            )}
          </div>
          <div className="space-y-2">
            {projectAutos.map(auto => {
              const isChecked = pauseAutoIds.includes(auto._id);
              const actNames = (auto.actionneurs || []).map(a => a.sn || a).join(', ');
              const creneauxDesc = (auto.creneaux || []).map(c => `${c.debut}→${c.fin}`).join(', ');

              // Vérifier si cette auto est directement impactée (a un actionneur en commun avec l'event)
              const actionneurNodes = nodes.filter(n => n.nodeType === 'actionneur');
              const eventActIds = actionneurNodes.map(n => n._id || n.id);
              const autoActIds = (auto.actionneurs || []).map(a => a._id || a);
              const isImpacted = eventActIds.some(eId =>
                autoActIds.some(aId => aId === eId) ||
                actionneurs.some(a => (a.id === eId || a._id === eId) && autoActIds.includes(a._id))
              );

              return (
                <label
                  key={auto._id}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    isChecked
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setPauseAutoIds(prev =>
                      prev.includes(auto._id)
                        ? prev.filter(id => id !== auto._id)
                        : [...prev, auto._id]
                    )}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 text-sm">{auto.nom}</p>
                      {isImpacted && (
                        <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">Impactée</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{actNames}</span>
                      {creneauxDesc && (
                        <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{creneauxDesc}</span>
                      )}
                    </div>
                  </div>
                  {isChecked && (
                    <span className="text-[11px] bg-amber-200 text-amber-800 px-2 py-1 rounded-md font-medium shrink-0">
                      Sera mise en pause
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => setPauseAutoIds(projectAutos.map(a => a._id))}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium underline"
            >
              Tout cocher
            </button>
            <button
              onClick={() => setPauseAutoIds([])}
              className="text-xs text-slate-400 hover:text-slate-600 font-medium underline"
            >
              Tout décocher
            </button>
          </div>
        </div>
      )}

      {/* Modal condition (nouvelle connexion) */}
      {showCondModal && pendingConn && (
        <ConditionModal
          conn={pendingConn}
          onValidate={validateCondition}
          onClose={() => { setShowCondModal(false); setPendingConn(null); }}
        />
      )}

      {/* Modal édition connexion existante */}
      {editingConn && (() => {
        const fromNode = nodes.find(n => n.instanceId === editingConn.fromId);
        const toNode = nodes.find(n => n.instanceId === editingConn.toId);
        const enriched = {
          ...editingConn,
          fromLabel: editingConn.fromLabel || fromNode?.id || fromNode?.label || "—",
          toLabel: editingConn.toLabel || toNode?.id || toNode?.label || "—",
        };
        return (
          <ConditionModal
            conn={enriched}
            initialCondition={editingConn.condition}
            initialValeur={editingConn.valeur}
            initialAction={editingConn.action}
            onValidate={({ condition, valeur, action }) => {
              setConnections(prev => prev.map(c =>
                c.id === editingConn.id ? { ...c, condition, valeur, action } : c
              ));
              setEditingConn(null);
            }}
            onClose={() => setEditingConn(null)}
          />
        );
      })()}

      {/* Modal conflit */}
      {conflictWarning && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                conflictWarning.some(c => c.severity === 'error') ? 'bg-red-100' : 'bg-amber-100'
              }`}>
                <AlertTriangle size={20} className={
                  conflictWarning.some(c => c.severity === 'error') ? 'text-red-600' : 'text-amber-600'
                } />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">
                  {conflictWarning.some(c => c.severity === 'error') ? t('createEvent.conflictsDetected') : t('createEvent.warnings')}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {conflictWarning.filter(c => c.severity === 'error').length > 0 && (
                    <span className="text-red-600 font-medium">{conflictWarning.filter(c => c.severity === 'error').length} {t('createEvent.errors')}</span>
                  )}
                  {conflictWarning.filter(c => c.severity === 'error').length > 0 && conflictWarning.filter(c => c.severity === 'warning').length > 0 && ' · '}
                  {conflictWarning.filter(c => c.severity === 'warning').length > 0 && (
                    <span className="text-amber-600 font-medium">{conflictWarning.filter(c => c.severity === 'warning').length} {t('createEvent.warningsCount')}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6 max-h-72 overflow-y-auto">
              {conflictWarning.map((c, i) => {
                const isError = c.severity === 'error';
                const typeLabels = {
                  auto_contradiction: t('createEvent.autoContradiction'),
                  event_condition_overlap: t('createEvent.eventConflict'),
                  event_different_capteurs: t('createEvent.conflictRisk'),
                  event_vs_automation: t('createEvent.automationConflict'),
                };
                return (
                  <div key={i} className={`border rounded-xl p-3 ${
                    isError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        isError ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'
                      }`}>
                        {typeLabels[c.type] || c.type}
                      </span>
                      {c.evenement && <span className="text-xs text-slate-500">→ {c.evenement.nom}</span>}
                      {c.automatisation && <span className="text-xs text-slate-500">→ {c.automatisation.nom}</span>}
                    </div>
                    <p className={`text-sm font-medium ${isError ? 'text-red-800' : 'text-amber-800'}`}>{c.message || c.detail}</p>
                    {c.actionneurLabel && (
                      <p className={`text-xs mt-1 ${isError ? 'text-red-600' : 'text-amber-600'}`}>
                        {t('createEvent.actuatorLabel')} <strong>{c.actionneurLabel}</strong>
                        {c.actionNouvelle && <> — {c.actionNouvelle} vs {c.actionExistante}</>}
                      </p>
                    )}
                    {c.creneau && (
                      <p className={`text-xs mt-0.5 ${isError ? 'text-red-600' : 'text-amber-600'}`}>
                        {t('createEvent.slotLabel')} {c.creneau} {c.jours && `(${c.jours})`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConflictWarning(null)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleForceSave}
                className="px-4 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700"
              >
                {t('createEvent.forceSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-[slideUp_0.3s_ease-out] ${
          toast.type === "success" ? "bg-green-600 text-white" :
          toast.type === "warning" ? "bg-amber-600 text-white" :
          "bg-red-600 text-white"
        }`}>
          {toast.type === "success" ? <CheckCircle size={18} /> : toast.type === "warning" ? <AlertTriangle size={18} /> : <X size={18} />}
          {toast.message}
        </div>
      )}
    </>
  );
}